/// <reference types="vite/client" />

/**
 * Application entry: the provider stack, in dependency order.
 *
 * Queries are outermost because the session is fetched through them; the
 * router is above the session so signing out can navigate; toasts are above
 * the session and the sync store so both can report failures; and an error
 * boundary wraps the lot, because a crash inside React unmounts the whole tree
 * and a blank white page is the one failure a user cannot act on.
 */

// First on purpose: it stamps the cached theme on <html> before any other
// module is evaluated, which is what keeps the first paint in the right
// palette without an inline script the content security policy would block.
import '@/lib/themeBoot'

import { Component, StrictMode } from 'react'
import type { ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'

import '@/design/base.css'

import { initTheme } from '@/lib/theme'
import { recordDiagnostic } from '@/lib/diagnostics'
import { AuthProvider } from '@/api/auth'
import { defaultQueryRetry } from '@/api/queries'
import { SyncProvider } from '@/api/store'
import { App } from '@/App'
import { Button, FullPageMessage, ToastProvider } from '@/components/ui'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The event stream keeps the application current, so refetching every
      // time the window is focused would only duplicate work the socket does.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: defaultQueryRetry,
    },
    mutations: {
      // A mutation that failed may have been applied. Retrying is the caller's
      // decision, not a default.
      retry: false,
    },
  },
})

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  override componentDidCatch(): void {
    recordDiagnostic('render_error')
    console.error('relaii: unhandled render error (details omitted to protect message content)')
  }

  private readonly retry = (): void => {
    this.setState({ error: null })
  }

  private readonly reload = (): void => {
    window.location.reload()
  }

  override render(): ReactNode {
    const { error } = this.state

    if (error === null) {
      return this.props.children
    }

    return (
      <FullPageMessage
        title="relaii ran into a problem"
        description={
          error.message === ''
            ? 'Something went wrong while drawing this screen.'
            : error.message
        }
        actions={
          <>
            <Button onClick={this.retry}>Try again</Button>
            <Button variant="primary" onClick={this.reload}>
              Reload relaii
            </Button>
          </>
        }
      />
    )
  }
}

initTheme()

const container = document.getElementById('root')

if (container === null) {
  throw new Error('relaii: index.html is missing its #root element.')
}

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ToastProvider>
            <AuthProvider>
              <SyncProvider>
                <App />
              </SyncProvider>
            </AuthProvider>
          </ToastProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)
